import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, TextField, Button, Switch, FormControlLabel,
  Alert, Snackbar, CircularProgress, Divider, Chip, IconButton,
  InputAdornment, MenuItem, Select, FormControl, InputLabel,
} from '@mui/material';
import {
  Save as SaveIcon, Send as SendIcon, Wifi as WifiIcon,
  Visibility, VisibilityOff, CheckCircle, Error as ErrorIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { API_BASE_URL } from '../../config/constants';

const getHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('backoffice_access_token')}`,
});

interface SmtpSettings {
  smtp_host: string;
  smtp_port: number;
  smtp_user: string;
  smtp_password: string;
  smtp_use_tls: boolean;
  smtp_use_ssl: boolean;
  email_from: string;
  email_from_name: string;
  email_enabled: boolean;
  email_test_mode: boolean;
  email_test_recipient: string;
}

const DEFAULT_SMTP: SmtpSettings = {
  smtp_host: '', smtp_port: 587, smtp_user: '', smtp_password: '',
  smtp_use_tls: true, smtp_use_ssl: false,
  email_from: '', email_from_name: 'Mowilex Customer Portal',
  email_enabled: false, email_test_mode: true, email_test_recipient: '',
};

const SystemConfigPage: React.FC = () => {
  const [smtp, setSmtp] = useState<SmtpSettings>(DEFAULT_SMTP);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [testRecipient, setTestRecipient] = useState('');
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'info' });

  const showSnack = (message: string, severity: 'success' | 'error' | 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/backoffice/system-config/smtp`, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        if (data.settings) {
          setSmtp({
            ...DEFAULT_SMTP,
            ...data.settings,
            smtp_password: '', // Don't populate masked password
          });
        }
      }
    } catch (err) {
      showSnack('Failed to load SMTP settings', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload: any = { ...smtp };
      // Only send password if user entered a new one
      if (!payload.smtp_password) {
        payload.smtp_password = null; // null = don't change
      }
      const res = await fetch(`${API_BASE_URL}/backoffice/system-config/smtp`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.success) {
        showSnack('SMTP settings saved successfully', 'success');
        loadSettings();
      } else {
        showSnack(data.detail || 'Failed to save', 'error');
      }
    } catch {
      showSnack('Failed to save SMTP settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await fetch(`${API_BASE_URL}/backoffice/system-config/smtp/test-connection`, {
        method: 'POST',
        headers: getHeaders(),
      });
      const data = await res.json();
      setTestResult({ success: data.success, message: data.message || data.detail || 'Unknown result' });
    } catch {
      setTestResult({ success: false, message: 'Network error — could not reach backend' });
    } finally {
      setTesting(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testRecipient) {
      showSnack('Enter a recipient email', 'info');
      return;
    }
    setSendingTest(true);
    setTestResult(null);
    try {
      const res = await fetch(`${API_BASE_URL}/backoffice/system-config/smtp/test-email`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ recipient: testRecipient }),
      });
      const data = await res.json();
      setTestResult({ success: data.success, message: data.message || data.detail || 'Unknown result' });
      if (data.success) showSnack('Test email sent!', 'success');
    } catch {
      setTestResult({ success: false, message: 'Network error' });
    } finally {
      setSendingTest(false);
    }
  };

  const updateField = (field: keyof SmtpSettings, value: any) => {
    setSmtp((prev) => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <SettingsIcon sx={{ fontSize: 28, color: 'primary.main' }} />
        <Typography variant="h5" fontWeight={700}>System Configuration</Typography>
      </Box>

      {/* SMTP Settings */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Email / SMTP Settings
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Configure the SMTP server used for all email notifications (password reset, order confirmation, etc.).
          These settings override the default .env configuration.
        </Typography>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          {/* Server */}
          <TextField
            label="SMTP Host"
            value={smtp.smtp_host}
            onChange={(e) => updateField('smtp_host', e.target.value)}
            placeholder="smtp.gmail.com"
            size="small"
            fullWidth
          />
          <TextField
            label="SMTP Port"
            type="number"
            value={smtp.smtp_port}
            onChange={(e) => updateField('smtp_port', parseInt(e.target.value) || 587)}
            size="small"
            fullWidth
          />

          {/* Credentials */}
          <TextField
            label="SMTP Username"
            value={smtp.smtp_user}
            onChange={(e) => updateField('smtp_user', e.target.value)}
            placeholder="your-email@gmail.com"
            size="small"
            fullWidth
          />
          <TextField
            label="SMTP Password"
            type={showPassword ? 'text' : 'password'}
            value={smtp.smtp_password}
            onChange={(e) => updateField('smtp_password', e.target.value)}
            placeholder="Leave blank to keep current"
            size="small"
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* Sender */}
          <TextField
            label="From Email"
            value={smtp.email_from}
            onChange={(e) => updateField('email_from', e.target.value)}
            placeholder="noreply@mowilex.com"
            size="small"
            fullWidth
          />
          <TextField
            label="From Name"
            value={smtp.email_from_name}
            onChange={(e) => updateField('email_from_name', e.target.value)}
            placeholder="Mowilex Customer Portal"
            size="small"
            fullWidth
          />
        </Box>

        {/* Security */}
        <Box sx={{ display: 'flex', gap: 3, mt: 2, flexWrap: 'wrap' }}>
          <FormControlLabel
            control={<Switch checked={smtp.smtp_use_tls} onChange={(e) => updateField('smtp_use_tls', e.target.checked)} />}
            label="Use TLS (STARTTLS)"
          />
          <FormControlLabel
            control={<Switch checked={smtp.smtp_use_ssl} onChange={(e) => updateField('smtp_use_ssl', e.target.checked)} />}
            label="Use SSL"
          />
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Email Controls */}
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          Email Controls
        </Typography>

        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={smtp.email_enabled}
                onChange={(e) => updateField('email_enabled', e.target.checked)}
                color="success"
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Email Sending
                <Chip
                  label={smtp.email_enabled ? 'ENABLED' : 'DISABLED'}
                  color={smtp.email_enabled ? 'success' : 'default'}
                  size="small"
                />
              </Box>
            }
          />
          <FormControlLabel
            control={
              <Switch
                checked={smtp.email_test_mode}
                onChange={(e) => updateField('email_test_mode', e.target.checked)}
                color="warning"
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Test Mode
                <Chip
                  label={smtp.email_test_mode ? 'ON' : 'OFF'}
                  color={smtp.email_test_mode ? 'warning' : 'default'}
                  size="small"
                />
              </Box>
            }
          />
        </Box>

        {smtp.email_test_mode && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>Test Mode is ON.</strong> All outgoing emails will be redirected to the test recipient below instead of actual recipients.
          </Alert>
        )}

        {smtp.email_test_mode && (
          <TextField
            label="Test Mode Recipient"
            value={smtp.email_test_recipient}
            onChange={(e) => updateField('email_test_recipient', e.target.value)}
            placeholder="test@mailinator.com"
            size="small"
            fullWidth
            sx={{ mb: 2 }}
            helperText="All emails will be redirected to this address when test mode is on"
          />
        )}

        {/* Save */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving}
          >
            Save SMTP Settings
          </Button>
        </Box>
      </Paper>

      {/* Test Section */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Test Email Delivery
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Verify your SMTP configuration by testing the connection and sending a test email.
        </Typography>

        {/* Test result */}
        {testResult && (
          <Alert
            severity={testResult.success ? 'success' : 'error'}
            icon={testResult.success ? <CheckCircle /> : <ErrorIcon />}
            sx={{ mb: 2 }}
            onClose={() => setTestResult(null)}
          >
            {testResult.message}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={testing ? <CircularProgress size={18} /> : <WifiIcon />}
            onClick={handleTestConnection}
            disabled={testing}
          >
            Test Connection
          </Button>

          <TextField
            label="Recipient Email"
            value={testRecipient}
            onChange={(e) => setTestRecipient(e.target.value)}
            placeholder="test@example.com"
            size="small"
            sx={{ minWidth: 280 }}
          />
          <Button
            variant="contained"
            color="success"
            startIcon={sendingTest ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
            onClick={handleSendTestEmail}
            disabled={sendingTest || !testRecipient}
          >
            Send Test Email
          </Button>
        </Box>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((p) => ({ ...p, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SystemConfigPage;
